import { memo, useMemo, useState, type FC } from "react";
import { useListWebhooks, type PlainWebhook } from "@/queries/webhook-queries";
import { Combobox } from "@/components/flexkit/combobox";
import { WEBHOOK_PROVIDER_CONFIG, WEBHOOK_TYPE_CONFIG } from "@/components/webhooks/webhook-config";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import IconCirclePlus from "central-icons/IconCirclePlus";
import { NewWebhookConfigurationDialog } from "@/components/webhooks/new-webhook-configuration-dialog/NewWebhookConfigurationDialog";

type WebhookSelectorProps = {
    selectedWebhookId?: string;
    disabled?: boolean;
    onSelectionChange: (webhookId: string | undefined) => void;
};

export const WebhookSelector: FC<WebhookSelectorProps> = memo(
    ({ selectedWebhookId, disabled = false, onSelectionChange }) => {
        const { data: webhooks, isLoading } = useListWebhooks();
        const [isDialogOpen, setIsDialogOpen] = useState(false);

        const selectedWebhook = useMemo(
            () => webhooks?.find((w) => w.id === selectedWebhookId),
            [webhooks, selectedWebhookId],
        );

        const handleValueChange = (value: string) => {
            onSelectionChange(value || undefined);
        };

        const handleOpenDialog = () => {
            setIsDialogOpen(true);
        };

        let comboboxContent: React.ReactNode;
        if (isLoading) {
            comboboxContent = <Combobox.Loading>Loading webhooks…</Combobox.Loading>;
        } else if (!webhooks || webhooks.length === 0) {
            comboboxContent = (
                <>
                    <Combobox.Empty>No webhooks configured.</Combobox.Empty>
                    <Combobox.Footer>
                        <ConfigureNewWebhookButton onClick={handleOpenDialog} data-tracking-id="opendialog" />
                    </Combobox.Footer>
                </>
            );
        } else {
            comboboxContent = (
                <>
                    <Combobox.List
                        items={webhooks}
                        searchKeys={["metadata.name", "metadata.description", "spec.scope"]}
                        noMatchesComponent={<Combobox.Empty>No matching webhooks.</Combobox.Empty>}
                    >
                        {(webhook) => <WebhookSelectItem key={webhook.id} webhook={webhook} />}
                    </Combobox.List>
                    <Combobox.Footer className="px-1 py-1.5">
                        <ConfigureNewWebhookButton
                            onClick={handleOpenDialog}
                            data-tracking-id="configure-new-webhook-webhook-selector"
                        />
                    </Combobox.Footer>
                </>
            );
        }

        return (
            <>
                <Combobox
                    value={selectedWebhookId ?? ""}
                    onValueChange={handleValueChange}
                    loading={isLoading}
                    disabled={disabled}
                >
                    <Combobox.Value>
                        {selectedWebhook ? (
                            <WebhookValueDisplay webhook={selectedWebhook} />
                        ) : (
                            <Combobox.ValueLabel className="text-content-muted">Select a webhook</Combobox.ValueLabel>
                        )}
                    </Combobox.Value>
                    <Combobox.Popover>{comboboxContent}</Combobox.Popover>
                </Combobox>
                <NewWebhookConfigurationDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
            </>
        );
    },
);
WebhookSelector.displayName = "WebhookSelector";

const WebhookValueDisplay: FC<{ webhook: PlainWebhook }> = memo(({ webhook }) => {
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;
    const name = webhook.metadata?.name || "Unnamed webhook";

    return (
        <Combobox.ValueLabel className="flex items-center gap-2">
            <ProviderIcon size="sm" className="flex-none text-content-secondary" />
            <span className="truncate">{name}</span>
        </Combobox.ValueLabel>
    );
});
WebhookValueDisplay.displayName = "WebhookValueDisplay";

const WebhookSelectItem: FC<{ webhook: PlainWebhook }> = memo(({ webhook }) => {
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const type = webhook.spec?.type ?? WebhookType.UNSPECIFIED;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;
    const name = webhook.metadata?.name || "Unnamed webhook";
    const typeName = WEBHOOK_TYPE_CONFIG[type].name;

    return (
        <Combobox.ListItem value={webhook.id}>
            <Combobox.ListItemLeadingIcon>
                <ProviderIcon size="sm" className="text-content-primary" />
            </Combobox.ListItemLeadingIcon>
            <Combobox.ListItemTitle>{name}</Combobox.ListItemTitle>
            <Combobox.ListItemDescription>{typeName}</Combobox.ListItemDescription>
        </Combobox.ListItem>
    );
});
WebhookSelectItem.displayName = "WebhookSelectItem";

type ConfigureNewWebhookButtonProps = {
    onClick: () => void;
    "data-tracking-id"?: string;
};

const ConfigureNewWebhookButton: FC<ConfigureNewWebhookButtonProps> = memo(
    ({ onClick, "data-tracking-id": trackingId }) => {
        return (
            <button
                type="button"
                onClick={onClick}
                className="flex w-full cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-base text-content-brand hover:bg-surface-hover"
                data-tracking-id={trackingId}
            >
                <IconCirclePlus size={16} className="flex-none" />
                <span>Configure new webhook</span>
            </button>
        );
    },
);
ConfigureNewWebhookButton.displayName = "ConfigureNewWebhookButton";
